import { lazy, Suspense } from 'react'
import { createBrowserRouter, useParams, useNavigate } from 'react-router-dom'
import App from './App'
import { TaskList } from './components/tasks/TaskList'
import { Skeleton } from './components/ui/Skeleton'
import { GroupSkeleton } from './components/groups/GroupSkeleton'

// Auto-reload on chunk load failure (stale deployment cache)
function lazyWithRetry<T extends React.ComponentType>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().then((module) => {
      // 청크 로드 성공 시 재시도 플래그 클리어
      sessionStorage.removeItem('chunk_reload')
      return module
    }).catch((err) => {
      // Only reload once to avoid infinite loops
      const key = 'chunk_reload'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
      }
      throw err
    })
  )
}

// Lazy-loaded route components (code splitting)
const TaskDetail = lazy(() =>
  import('./components/tasks/TaskDetail').then((m) => ({ default: m.TaskDetail }))
)
const CalendarView = lazyWithRetry(() =>
  import('./components/calendar/CalendarView').then((m) => ({ default: m.CalendarView }))
)
const GroupPage = lazyWithRetry(() =>
  import('./components/groups/GroupPage').then((m) => ({ default: m.GroupPage }))
)
const GroupDetailPage = lazyWithRetry(() =>
  import('./components/groups/GroupDetailPage').then((m) => ({ default: m.GroupDetailPage }))
)
const SharedGroupDetailPage = lazyWithRetry(() =>
  import('./components/groups/SharedGroupDetailPage').then((m) => ({ default: m.SharedGroupDetailPage }))
)
const FullScreenChat = lazyWithRetry(() =>
  import('./components/groups/FullScreenChat').then((m) => ({ default: m.FullScreenChat }))
)
const ProfilePage = lazyWithRetry(() =>
  import('./components/profile/ProfilePage').then((m) => ({ default: m.ProfilePage }))
)
const AIChatPage = lazyWithRetry(() =>
  import('./components/ai/AIChatPage').then((m) => ({ default: m.AIChatPage }))
)
const ChatListPage = lazyWithRetry(() =>
  import('./components/chat/ChatListPage').then((m) => ({ default: m.ChatListPage }))
)
const ActivityLogPage = lazyWithRetry(() =>
  import('./components/profile/ActivityLogPage').then((m) => ({ default: m.ActivityLogPage }))
)
const OAuthCallback = lazyWithRetry(() => import('./pages/OAuthCallback'))
const JoinGroup = lazyWithRetry(() => import('./pages/JoinGroup'))
// H5: 404 + 라우트별 에러 페이지
const ShareTargetPage = lazyWithRetry(() => import('./pages/ShareTarget').then(m => ({ default: m.ShareTarget })))
const NotFound = lazyWithRetry(() => import('./pages/NotFound'))
const RouteError = lazyWithRetry(() => import('./pages/RouteError'))

function LazyFallback() {
  return (
    <div className="p-4 space-y-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  )
}

function TaskDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  if (!id) return null
  return <Suspense fallback={<LazyFallback />}><TaskDetail taskId={Number(id)} onBack={() => navigate(-1)} /></Suspense>
}

// H5: 라우트별 에러 UI
function RouteErrorElement() {
  return <Suspense fallback={<LazyFallback />}><RouteError /></Suspense>
}

export const router = createBrowserRouter([
  // 전체 화면 채팅 — App 레이아웃(헤더/하단메뉴) 밖
  {
    path: '/groups/shared/:id/chat',
    element: <Suspense fallback={<LazyFallback />}><FullScreenChat /></Suspense>,
    errorElement: <RouteErrorElement />,
  },
  {
    path: '/',
    element: <App />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        index: true,
        element: <TaskList />,
      },
      {
        path: 'tasks',
        element: <TaskList />,
      },
      {
        path: 'task/:id',
        element: <TaskDetailPage />,
      },
      {
        path: 'chat',
        element: <Suspense fallback={<LazyFallback />}><div className="content-fade-in"><ChatListPage /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'calendar',
        element: <Suspense fallback={<LazyFallback />}><div className="content-fade-in"><CalendarView /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'groups',
        element: <Suspense fallback={<GroupSkeleton />}><div className="content-fade-in"><GroupPage /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'groups/shared/:id',
        element: <Suspense fallback={<LazyFallback />}><div className="content-fade-in"><SharedGroupDetailPage /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      // groups/shared/:id/chat → 레이아웃 밖 독립 라우트로 이동
      {
        path: 'groups/:id',
        element: <Suspense fallback={<LazyFallback />}><div className="content-fade-in"><GroupDetailPage /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'ai',
        element: <Suspense fallback={<LazyFallback />}><div className="content-fade-in"><AIChatPage /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'profile',
        element: <Suspense fallback={<LazyFallback />}><div className="content-fade-in"><ProfilePage /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      {
        path: 'activity',
        element: <Suspense fallback={<LazyFallback />}><div className="content-fade-in"><ActivityLogPage /></div></Suspense>,
        errorElement: <RouteErrorElement />,
      },
      {
        // H5: 404 catch-all
        path: '*',
        element: <Suspense fallback={<LazyFallback />}><NotFound /></Suspense>,
      },
    ],
  },
  {
    path: '/oauth/callback',
    element: <Suspense fallback={<LazyFallback />}><OAuthCallback /></Suspense>,
  },
  {
    path: '/join/:code',
    element: <Suspense fallback={<LazyFallback />}><JoinGroup /></Suspense>,
  },
  {
    path: '/share-target',
    element: <Suspense fallback={<LazyFallback />}><ShareTargetPage /></Suspense>,
  },
])
